import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import ComponentHeading from 'components/Heading/Heading';

// Scripts
import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentStatisticPanel } from 'graphqlTypes';
import type { FC } from 'react';

interface StatisticsPanelProps extends FlexProps, ContentfulComponentStatisticPanel {
  backgroundIsDark: boolean;
  statsHeading?: ContentfulComponentStatisticPanel['heading'];
  isReferenced?: boolean;
}

const ComponentStatisticPanel: FC<StatisticsPanelProps> = ({
  statsHeading,
  statistic,
  backgroundIsDark,
  isReferenced,
  ...props
}) => {
  const determineValueColor = (value: string) => {
    switch (value) {
      case 'Blue':
        return 'blue-600';
      case 'Yellow':
        return 'sunrise-500';
      case 'Green':
        return 'green-300';
      case 'Red':
        return 'sunset-400';
      default:
        return 'gray-900';
    }
  };

  const statLength = statistic?.length || 3;

  return (
    <Flex
      flexDirection="column"
      gap="40px"
      width="100%"
      sm={{ gap: '56px', flexDirection: isReferenced ? 'row' : 'column' }}
      lg={{ gap: '64px' }}
      {...props}
    >
      {statsHeading && <ComponentHeading {...statsHeading} backgroundIsDark={backgroundIsDark} />}
      {statistic && (
        <Flex
          flexDirection={isReferenced ? 'column' : 'row'}
          flexWrap="wrap"
          justifyContent="space-between"
          width="100%"
          gap="30px"
          sm={{ flexDirection: 'row' }}
        >
          {statistic.map(item => (
            <Flex
              key={item?.heading}
              flexDirection={isReferenced ? 'row' : 'column'}
              alignItems="center"
              textAlign={isReferenced ? 'left' : 'center'}
              gap="12px"
              width="100%"
              maxWidth="100%"
              sm={{
                gap: '16px',
                maxWidth: isReferenced
                  ? 'calc(50% - 15px)'
                  : `calc((100% - ${(statLength - 1) * 30}px) / ${statLength})`,
                flexDirection: isReferenced ? 'row' : 'column',
              }}
            >
              {item?.heading && (
                <Heading
                  as="h4"
                  textStyle={isReferenced ? 'h3' : 'h2'}
                  fontWeight="bold"
                  textColor={backgroundIsDark ? 'common-white' : 'gray-900'}
                  md={{ textStyle: isReferenced ? 'h2' : 'h1', fontWeight: 'bold' }}
                  lg={{ textStyle: isReferenced ? 'h2' : 'h1', fontWeight: 'bold' }}
                >
                  {item?.value === '$' ? (
                    <>
                      {item?.value && (
                        <Text textColor={determineValueColor(item?.valueColor || 'Blue')}>{item.value}</Text>
                      )}
                      {item.heading}
                    </>
                  ) : (
                    <>
                      {item.heading}
                      {item?.value && (
                        <Text textColor={determineValueColor(item?.valueColor || 'Blue')}>{item.value}</Text>
                      )}
                    </>
                  )}
                </Heading>
              )}
              <Flex
                flexDirection="column"
                gap="8px"
                fontSize="textXs"
                textColor={backgroundIsDark ? 'common-white' : 'gray-900'}
                sm={{ fontSize: 'textSm' }}
              >
                {item?.body && richTextParser(item.body)}
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default ComponentStatisticPanel;
